import {
  Box,
  FlexBox,
  InputSelectionList,
  InputSelectionListProps,
  SelectionList,
  SimplePagination,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import type { Queue } from 'lib-shared-types-api';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Waypoint } from 'react-waypoint';

import { queuesApi } from 'apis/queues';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import styles from './inputQueuesSelect.module.scss';

export type BasicQueueSelectItem = Pick<Queue, 'id' | 'name'>;

type InputQueuesSelectProps<TQueueSelectItem extends BasicQueueSelectItem> =
  Omit<
    InputSelectionListProps<TQueueSelectItem>,
    'getItemTitleByValue' | 'equalityFn'
  > & {
    /** Whether to use infinite scroll instead of manual pagination. */
    infiniteScroll?: boolean;
  };

/** Represents the component for selecting a queue. */
export const InputQueuesSelect = <
  TQueueSelectItem extends BasicQueueSelectItem = Queue,
>({
  multiple,
  onToggleDropdown,
  placeholder,
  infiniteScroll,
  dropdownProps,
  selectionListProps,
  ...inputSelectionListProps
}: InputQueuesSelectProps<TQueueSelectItem>): JSX.Element => {
  const [search, setSearch] = useState<{
    readonly term: string;
    readonly debounceTime?: number;
  }>({ term: '' });
  const {
    arg,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items: queues,
    changeArg,
    nextPage,
    previousPage,
  } = usePaginatedEndpoint({
    endpoint: queuesApi.endpoints.readQueues,
    itemsSelector: ({ data: { queues } }) => queues,
    initialArg: {
      isArchived: false,
      sort: { key: 'name', direction: 'asc' },
    },
    infinite: infiniteScroll,
  });

  /** Handles searching the queues. */
  const handleFilterValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch({ term: event.target.value });
    },
    [],
  );

  /** Handles toggling the dropdown of the select component. */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setSearch({ term: '', debounceTime: 0 });
      }
      onToggleDropdown?.(isOpen);
    },
    [onToggleDropdown],
  );

  // Changes the arg when search term changes.
  useEffect(() => {
    if (search.term !== arg?.searchTerm) {
      changeArg(
        {
          searchTerm: search.term,
          offset: undefined,
          limit: undefined,
        },
        { debounceTime: search.debounceTime ?? 300 },
      );
    }
    // Don't rely on arg as dependency because we change the arg inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeArg, search]);

  const paginationButtons = useMemo(() => {
    if (infiniteScroll) {
      return null;
    }

    return (
      <Box padding="250" className={styles.paginationButtonsContainer}>
        <SimplePagination
          nextButtonDisabled={!hasNextPage}
          nextButtonClick={nextPage}
          previousButtonDisabled={!hasPreviousPage}
          previousButtonClick={previousPage}
          loading={asyncStatus === 'pending'}
        />
      </Box>
    );
  }, [
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    infiniteScroll,
    nextPage,
    previousPage,
  ]);

  const paginationInfiniteScroll = useMemo(() => {
    if (!infiniteScroll) {
      return null;
    }

    return asyncStatus === 'pending' ? (
      <FlexBox
        justifyContent="center"
        alignItems="center"
        style={{ height: '32px' }}
      >
        <Spinner size={20} />
      </FlexBox>
    ) : hasNextPage ? (
      <Waypoint onEnter={nextPage} />
    ) : null;
  }, [asyncStatus, hasNextPage, infiniteScroll, nextPage]);

  const items = useMemo(() => {
    if (!queues.length && asyncStatus !== 'pending') {
      return (
        <Box padding="200">
          <Text.Paragraph>
            {search.term ? 'No queues found' : 'No queues available'}
          </Text.Paragraph>
        </Box>
      );
    }

    return (
      <>
        {queues.map((queue) => (
          <SelectionList.Item key={queue.id} value={queue}>
            <Text.Paragraph>{queue.name}</Text.Paragraph>
          </SelectionList.Item>
        ))}

        {paginationInfiniteScroll}
      </>
    );
  }, [asyncStatus, paginationInfiniteScroll, queues, search.term]);

  return (
    <InputSelectionList<TQueueSelectItem>
      {...inputSelectionListProps}
      equalityFn={(a, b) => a.id === b.id}
      getItemTitleByValue={(value) => (
        <Text.Paragraph>{value?.name}</Text.Paragraph>
      )}
      multiple={multiple}
      placeholder={placeholder ?? multiple ? 'Select queues' : 'Select a queue'}
      onToggleDropdown={handleToggle}
      filter={{
        value: search.term,
        onChange: handleFilterValueChange,
      }}
      selectionListProps={{
        ...selectionListProps,
        style: {
          maxHeight: 8 * 36, // 8 visible items
          overflow: 'auto',
          ...selectionListProps?.style,
        },
      }}
      dropdownProps={{
        ...dropdownProps,
        style: { minWidth: 256, ...dropdownProps?.style },
      }}
      slotAfterList={paginationButtons}
    >
      {items}
    </InputSelectionList>
  );
};
